import { useState, useRef, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { LifeBuoy, Send, Loader2, X, MailPlus, CheckCircle2, HelpCircle } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WELCOME = {
  role: "assistant",
  content:
    "Hi, ich bin der TradeMind-Support. Frag mich zu Bedienung, Broker-Anbindung, Trade-Wizard, Strategien, Steuer-Tool oder Datenschutz. Wenn ich nicht weiterhelfe, kannst du mit einem Klick ein Ticket ans Support-Team senden.",
};

const SUGGESTIONS = [
  "Wie verbinde ich meinen Broker?",
  "Wie erfasse ich einen Trade manuell?",
  "Wo finde ich meinen Steuerbericht?",
  "Wie funktioniert der Freistellungsauftrag?",
];

export default function SupportWidget() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([WELCOME]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [ticketOpen, setTicketOpen] = useState(false);
  const [ticket, setTicket] = useState({ subject: "", message: "", contact_email: "" });
  const [sending, setSending] = useState(false);
  const [ticketOk, setTicketOk] = useState(null);
  const sessionId = useRef(`support_${Date.now()}`);
  const scrollerRef = useRef(null);
  const showEscalate = messages.some((m) => m.role === "user");

  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener("trademind:open-support", handler);
    return () => window.removeEventListener("trademind:open-support", handler);
  }, []);

  useEffect(() => {
    if (scrollerRef.current) scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
  }, [messages, loading, open]);

  useEffect(() => {
    if (ticketOpen && user && user !== false) {
      setTicket((t) => ({ ...t, contact_email: t.contact_email || user.email || "" }));
    }
  }, [ticketOpen, user]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || loading) return;
    const next = [...messages, { role: "user", content: msg }];
    setMessages(next);
    setInput("");
    setLoading(true);
    try {
      const res = await axios.post(`${API}/support/chat`, {
        session_id: sessionId.current,
        messages: next.map(({ role, content }) => ({ role, content })),
      }, { withCredentials: true, timeout: 60000 });
      const reply = res.data?.reply?.toString().trim();
      if (!reply) throw new Error("empty");
      setMessages([...next, { role: "assistant", content: reply }]);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Der Support ist gerade nicht erreichbar. Bitte in einem Moment erneut versuchen.";
      toast.error("Support nicht erreichbar", { description: detail });
    } finally { setLoading(false); }
  };

  const openTicket = () => {
    const lastUserMsg = [...messages].reverse().find((m) => m.role === "user")?.content || "";
    setTicket({
      subject: lastUserMsg.slice(0, 100) || "",
      message: lastUserMsg,
      contact_email: user?.email || "",
    });
    setTicketOk(null);
    setTicketOpen(true);
  };

  const submitTicket = async () => {
    if (ticket.subject.trim().length < 3) return toast.error("Betreff bitte etwas ausführlicher.");
    if (ticket.message.trim().length < 10) return toast.error("Bitte beschreibe dein Anliegen etwas genauer (min. 10 Zeichen).");
    setSending(true);
    try {
      const res = await axios.post(`${API}/support/ticket`, {
        subject: ticket.subject.trim(),
        message: ticket.message.trim(),
        contact_email: ticket.contact_email.trim() || undefined,
        conversation: messages.map(({ role, content }) => ({ role, content })),
      }, { withCredentials: true, timeout: 30000 });
      setTicketOk(res.data);
      toast.success("Ticket versendet", { description: `Ticket-ID: ${res.data.ticket_id}` });
    } catch (e) {
      const detail = e?.response?.data?.detail || "Bitte in einem Moment erneut versuchen.";
      toast.error("Ticket konnte nicht versendet werden", { description: detail });
    } finally { setSending(false); }
  };

  if (!user || user === false) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Support öffnen"
        className="fixed bottom-6 right-6 z-40 h-14 w-14 rounded-full bg-primary text-primary-foreground shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all grid place-items-center border border-primary/50"
        data-testid="support-widget-fab"
      >
        <LifeBuoy className="h-6 w-6" strokeWidth={1.5} />
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl h-[70vh] p-0 flex flex-col gap-0" data-testid="support-dialog">
          <DialogHeader className="p-5 border-b border-border">
            <DialogTitle className="flex items-center gap-2">
              <LifeBuoy className="h-5 w-5 text-primary" strokeWidth={1.5} /> Support
            </DialogTitle>
            <DialogDescription>
              Erst die KI — wenn die nicht weiterkommt, geht dein Ticket direkt ans Support-Team.
            </DialogDescription>
          </DialogHeader>

          <div ref={scrollerRef} className="flex-1 overflow-y-auto px-5 py-4 space-y-3" data-testid="support-messages">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                  m.role === "user" ? "bg-primary text-primary-foreground" : "bg-secondary/70 text-foreground border border-border"
                }`} style={{ whiteSpace: "pre-wrap" }}>
                  {m.content}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> Support denkt nach…
              </div>
            )}
            {messages.length === 1 && !loading && (
              <div className="pt-2">
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1.5">
                  <HelpCircle className="h-3 w-3" /> Häufige Fragen
                </div>
                <div className="flex flex-wrap gap-2">
                  {SUGGESTIONS.map((s, i) => (
                    <button
                      key={i}
                      onClick={() => send(s)}
                      className="text-xs px-3 py-2 rounded-full border border-border hover:bg-secondary transition-colors"
                      data-testid={`support-suggestion-${i}`}
                    >{s}</button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="border-t border-border p-4 space-y-2">
            {showEscalate && (
              <div className="flex justify-end">
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-full h-8 text-xs"
                  onClick={openTicket}
                  data-testid="support-escalate-btn"
                >
                  <MailPlus className="mr-1.5 h-3.5 w-3.5" strokeWidth={1.5} />
                  Ticket ans Support-Team senden
                </Button>
              </div>
            )}
            <div className="flex items-end gap-2">
              <Textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder="Frage stellen …"
                rows={2}
                className="min-h-[48px] resize-none"
                data-testid="support-input"
              />
              <Button onClick={() => send()} disabled={loading || !input.trim()} className="rounded-full h-11 px-4" data-testid="support-send">
                <Send className="h-4 w-4" strokeWidth={1.5} />
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={ticketOpen} onOpenChange={(v) => { setTicketOpen(v); if (!v) setTicketOk(null); }}>
        <DialogContent className="max-w-lg" data-testid="support-ticket-dialog">
          {ticketOk ? (
            <div className="text-center py-6" data-testid="support-ticket-success">
              <CheckCircle2 className="mx-auto h-12 w-12 text-[hsl(122_39%_60%)]" strokeWidth={1.5} />
              <div className="font-heading text-lg mt-3">Dein Ticket ist unterwegs</div>
              <p className="text-sm text-muted-foreground mt-2">
                Ticket-ID: <span className="font-mono text-foreground">{ticketOk.ticket_id}</span>
              </p>
              {ticketOk.confirmation_sent ? (
                <p className="text-xs text-muted-foreground mt-3">
                  Wir haben dir eine Bestätigung an {ticket.contact_email} geschickt. Das Support-Team meldet sich innerhalb 24&nbsp;h.
                </p>
              ) : (
                <p className="text-xs text-muted-foreground mt-3">
                  Das Support-Team meldet sich per E-Mail an {ticket.contact_email}.
                </p>
              )}
              <Button className="rounded-full mt-6" onClick={() => { setTicketOpen(false); setOpen(false); }} data-testid="support-ticket-close">
                Schließen
              </Button>
            </div>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle>Ticket ans Support-Team</DialogTitle>
                <DialogDescription>Bisherige KI-Konversation wird automatisch mitgeschickt.</DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">Betreff</Label>
                  <Input
                    value={ticket.subject}
                    onChange={(e) => setTicket({ ...ticket, subject: e.target.value })}
                    placeholder="Kurz: Worum geht es?"
                    className="mt-1.5"
                    data-testid="support-ticket-subject"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">Dein Anliegen</Label>
                  <Textarea
                    value={ticket.message}
                    onChange={(e) => setTicket({ ...ticket, message: e.target.value })}
                    placeholder="Beschreibe dein Problem — was hast du versucht, was funktioniert nicht?"
                    rows={5}
                    className="mt-1.5"
                    data-testid="support-ticket-message"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">Antwort-E-Mail</Label>
                  <Input
                    type="email"
                    value={ticket.contact_email}
                    onChange={(e) => setTicket({ ...ticket, contact_email: e.target.value })}
                    className="mt-1.5"
                    data-testid="support-ticket-email"
                  />
                  <p className="text-[11px] text-muted-foreground mt-1">Antwort geht standardmäßig an deine Konto-Adresse.</p>
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <Button variant="ghost" onClick={() => setTicketOpen(false)} data-testid="support-ticket-cancel">Abbrechen</Button>
                  <Button onClick={submitTicket} disabled={sending} className="rounded-full" data-testid="support-ticket-submit">
                    {sending ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Versendet…</>) : (<><MailPlus className="h-4 w-4 mr-2" strokeWidth={1.5} /> Ticket senden</>)}
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
